import React from 'react';
import { generatePath, Link } from 'react-router-dom';
import classNames from 'classnames';
import format from 'date-fns/format';
import { CheckedIcon } from '@/assets/images';
import Loader from '@/components/common/Loader';
import AnalyticsLocation from '@/constants/AnalyticsLocation';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import LoaderSizes from '@/constants/ui/LoaderSizes';
import { DEFAULT_DATE_FORMAT } from '@/lib/constants/common';
import translate from '@/lib/intl/translate';
import { useEntityDetails } from '@/modules/EntityDetails';
import { useGoogleAnalytics } from '@/modules/GoogleAnalytics';
import { dateStringToDate } from '@/utils/dates';
import useEmployeeTasksSummary from '../lib/useEmployeeTasksSummary';
import { AnalyticsTarget } from '../lib/useTaskAnalytics';

export default function EmployeeTasksSummary() {
  const { linkClickedEvent } = useGoogleAnalytics();
  const { openTaskEntity } = useEntityDetails();
  const { tasks, employeeTotalTasks, orgTotalTasks, userId } =
    useEmployeeTasksSummary();

  const taskListClicked = (entity) => {
    linkClickedEvent(
      AnalyticsLocation.HOMEPAGE,
      `${AnalyticsTarget.VIEW_TASKS} | ${entity}`,
    );
  };

  const onTaskClick = (task) => {
    linkClickedEvent(
      AnalyticsLocation.HOMEPAGE,
      `${AnalyticsTarget.VIEW_TASK} | ${task.taskTitle}`,
    );
    openTaskEntity(task.taskId);
  };

  if (employeeTotalTasks === undefined)
    return <Loader isCentered size={LoaderSizes.SM} />;

  return (
    <div className="mb-6 rounded-lg md:border md:border-grey-300 md:px-4 md:py-3">
      <div className="flex items-end justify-between text-base type-h3">
        {translate('TASKS_MY_TASKS')} • {employeeTotalTasks} {translate('OPEN')}
        <Link
          to={{
            pathname: generatePath(AuthRoutes.TASKS),
            search: `?user_id=${userId}`,
          }}
          onClick={() => taskListClicked('employee')}
          className="text-primary-main"
        >
          {translate('VIEW_ALL')}
        </Link>
      </div>
      <div className="-mx-3 flex flex-row flex-nowrap overflow-x-auto p-3 hide-scrollbar md:mx-0 md:flex-col md:p-0">
        {tasks?.length ? (
          tasks.map((task, i) => (
            <button
              key={task.taskId}
              onClick={() => onTaskClick(task)}
              type="button"
            >
              <div
                className={classNames(
                  { 'ml-4 md:ml-0': i, 'md:mt-4': i === 0 },
                  'mb-2 flex w-64 flex-col rounded-lg border border-grey-300 bg-grey-100 px-3 py-2 text-left text-xs font-normal hover:bg-grey-300 md:w-full',
                )}
              >
                <div className="mb-1 line-clamp-2">{task.taskTitle}</div>
                <div className="text-grey-900">
                  {task.dueDate &&
                    format(dateStringToDate(task.dueDate), DEFAULT_DATE_FORMAT)}
                </div>
              </div>
            </button>
          ))
        ) : (
          <div className="my-4 mr-3.5 flex w-full items-center justify-center rounded-lg border border-grey-300 bg-grey-100 p-3 text-xs font-normal md:mr-0">
            <CheckedIcon />
            <span className="ml-2">{translate('TASKS_CAUGHT_UP')}</span>
          </div>
        )}
      </div>
      <Link
        to={{
          pathname: generatePath(AuthRoutes.TASKS),
          search: '?user_id=all',
        }}
        onClick={() => taskListClicked('org')}
        className="text-sm font-normal text-primary-main"
      >
        {orgTotalTasks} {translate('TASKS_ORG_TASKS')}
      </Link>
    </div>
  );
}
